/**
 * Exportar widget o reporte a PNG, PDF o Excel.
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { engineFetch, engineErrorMessage, buildWidgetQueryPayload, sourceRequestFields, isDecorationType, isContainerType, getWidgetStyle } from './engineConfig';
import { visibleMetricList } from './fieldActions';
import { getAlias } from './filterUtils';

/** Nombre de archivo seguro para descarga. */
export function sanitizeFilename(name, fallback = 'reporte') {
    const raw = String(name || fallback).trim() || fallback;
    return raw.replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || fallback;
}

/** Nombre de hoja Excel único (máx. 31 caracteres). */
export function uniqueSheetName(title, fallback, used) {
    let base = String(title || fallback || 'Hoja')
        .replace(/[\\/?*[\]]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 31) || fallback || 'Hoja';
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
        const suffix = ` (${n})`;
        name = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
        n += 1;
    }
    used.add(name.toLowerCase());
    return name;
}

function ignoreExportUi(el) {
    return el?.getAttribute?.('data-export-ignore') === 'true';
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Imagen → data URL para que html2canvas la pinte. Primero se intenta directo (si el
 * servidor manda CORS); las subidas al Creador se piden al backend, porque el bucket
 * de Storage no manda CORS y sin esto la imagen salía en blanco en el PDF.
 */
async function inlineImageSource(src, cache) {
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) return src;
    if (cache.has(src)) return cache.get(src);
    const attempt = (async () => {
        try {
            const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
            if (res.ok) return await blobToDataUrl(await res.blob());
        } catch (err) { /* sin CORS: se intenta por el backend */ }
        if (/^https:\/\/firebasestorage\.googleapis\.com\//.test(src)) {
            try {
                const res = await engineFetch(`/api/export/asset?url=${encodeURIComponent(src)}`);
                if (res.ok) return await blobToDataUrl(await res.blob());
            } catch (err) { /* se exporta sin esta imagen */ }
        }
        return null;
    })();
    cache.set(src, attempt);
    return attempt;
}

const CSS_URL = /url\(["']?(https?:[^"')]+)["']?\)/;

/**
 * Marca imágenes y fondos con URL del elemento y los convierte a data URL. Devuelve la
 * función que, sobre el documento clonado por html2canvas, pone cada data URL en su sitio.
 */
async function prepareImages(element) {
    const cache = new Map();
    const marks = [];
    element.querySelectorAll('img').forEach((img, index) => {
        const src = img.currentSrc || img.src;
        if (!src || src.startsWith('data:')) return;
        img.setAttribute('data-export-img', String(index));
        marks.push({ kind: 'img', key: String(index), node: img, src });
    });
    [element, ...element.querySelectorAll('[style*="url("]')].forEach((node, index) => {
        const match = CSS_URL.exec(node.style?.backgroundImage || '');
        if (!match) return;
        node.setAttribute('data-export-bg', String(index));
        marks.push({ kind: 'bg', key: String(index), node, src: match[1] });
    });
    const resolved = await Promise.all(marks.map(mark => inlineImageSource(mark.src, cache)));
    const missing = resolved.filter(value => !value).length;

    const applyToClone = (doc) => {
        marks.forEach((mark, i) => {
            const data = resolved[i];
            if (mark.kind === 'bg') {
                const node = doc.querySelector(`[data-export-bg="${mark.key}"]`);
                if (node) node.style.backgroundImage = data ? `url("${data}")` : 'none';
                return;
            }
            const img = doc.querySelector(`[data-export-img="${mark.key}"]`);
            if (!img) return;
            if (!data) {
                img.style.visibility = 'hidden';
                return;
            }
            /* html2canvas no respeta object-fit: la imagen se pinta como fondo con el mismo ajuste. */
            const fit = img.style.objectFit;
            if (fit && fit !== 'fill') {
                const box = doc.createElement('div');
                box.style.cssText = img.style.cssText;
                box.style.backgroundImage = `url("${data}")`;
                box.style.backgroundRepeat = 'no-repeat';
                box.style.backgroundSize = fit === 'none' ? 'auto' : (fit === 'scale-down' ? 'contain' : fit);
                box.style.backgroundPosition = img.style.objectPosition || 'center';
                img.replaceWith(box);
                return;
            }
            img.src = data;
        });
    };
    const cleanup = () => {
        marks.forEach(mark => mark.node.removeAttribute(mark.kind === 'img' ? 'data-export-img' : 'data-export-bg'));
    };
    return { applyToClone, cleanup, missing };
}

/** Cajas (px desde el borde superior del elemento) que un salto de página no debe partir. */
function measureBreakBoxes(element) {
    const origin = element.getBoundingClientRect().top;
    const boxes = [];
    element.querySelectorAll('[data-export-block], .react-grid-item, tr, [data-export-keep]').forEach((node) => {
        if (node.closest('[data-export-ignore="true"]')) return;
        const rect = node.getBoundingClientRect();
        if (rect.height < 2) return;
        boxes.push({ top: rect.top - origin, bottom: rect.bottom - origin, row: node.tagName === 'TR' });
    });
    return boxes;
}

/**
 * Captura DOM con html2canvas (imágenes incrustadas, sin la UI marcada data-export-ignore).
 * withLayout: además devuelve las cajas que no deben partirse y el alto en px CSS.
 */
export async function captureElementCanvas(element, { backgroundColor = '#ffffff', scale = 2, withLayout = false } = {}) {
    if (!element) throw new Error('No hay elemento para capturar.');
    const prevOverflow = element.style.overflow;
    const prevHeight = element.style.height;
    const images = await prepareImages(element);
    element.style.overflow = 'visible';
    element.style.height = 'auto';
    try {
        const layout = withLayout
            ? { boxes: measureBreakBoxes(element), height: element.scrollHeight, width: element.getBoundingClientRect().width }
            : null;
        const canvas = await html2canvas(element, {
            scale,
            useCORS: true,
            allowTaint: false,
            backgroundColor,
            logging: false,
            ignoreElements: ignoreExportUi,
            onclone: images.applyToClone,
            windowWidth: Math.max(element.scrollWidth, element.clientWidth),
            windowHeight: Math.max(element.scrollHeight, element.clientHeight)
        });
        return withLayout ? { canvas, layout, missingImages: images.missing } : canvas;
    } finally {
        element.style.overflow = prevOverflow;
        element.style.height = prevHeight;
        images.cleanup();
    }
}

function canvasToPngUrl(canvas) {
    return canvas.toDataURL('image/png');
}

function downloadDataUrl(dataUrl, filename) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/** Descarga PNG del elemento. */
export async function exportElementToPng(element, filename, options = {}) {
    const canvas = await captureElementCanvas(element, options);
    downloadDataUrl(canvasToPngUrl(canvas), filename.endsWith('.png') ? filename : `${filename}.png`);
}

/**
 * Cortes de página en px CSS. Cada corte se sube al inicio del primer elemento que
 * quedaría partido (si cabe en una hoja); en elementos más altos que una hoja, como una
 * tabla larga, se corta entre filas. Nunca se sube a menos de un 30 % de la hoja.
 */
export function planPageBreaks(totalHeight, pageHeight, boxes = []) {
    const cuts = [];
    let start = 0;
    let guard = 0;
    while (start < totalHeight - 1 && guard < 500) {
        guard += 1;
        let cut = start + pageHeight;
        if (cut >= totalHeight) {
            cuts.push([start, totalHeight]);
            break;
        }
        const floor = start + pageHeight * 0.3;
        for (let pass = 0; pass < 30; pass += 1) {
            const line = cut;
            const crossing = boxes.filter(box => box.top < line - 0.5 && box.bottom > line + 0.5 && box.top > floor);
            const whole = crossing.filter(box => !box.row && box.bottom - box.top <= pageHeight);
            const rows = crossing.filter(box => box.row);
            const pick = whole.length ? whole : rows;
            if (!pick.length) break;
            const next = Math.min(...pick.map(box => box.top));
            if (next >= cut) break;
            cut = next;
        }
        cuts.push([start, cut]);
        start = cut;
    }
    return cuts;
}

const PDF_MARGIN_X = 10;
const PDF_HEADER_H = 14;
const PDF_FOOTER_H = 12;
const PDF_FOOTER_TEXT = 'Generado por Central Media';

function drawHeaderFooter(pdf, { title, subtitle }) {
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const total = pdf.getNumberOfPages();
    const stamp = new Date().toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
    for (let page = 1; page <= total; page += 1) {
        pdf.setPage(page);
        pdf.setDrawColor(218, 220, 224);
        pdf.setLineWidth(0.2);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(32, 33, 36);
        const maxTitle = pageW - PDF_MARGIN_X * 2 - 60;
        pdf.text(pdf.splitTextToSize(title || 'Reporte', maxTitle)[0], PDF_MARGIN_X, 8);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(95, 99, 104);
        if (subtitle) pdf.text(pdf.splitTextToSize(subtitle, maxTitle)[0], PDF_MARGIN_X, 11.5);
        pdf.text(stamp, pageW - PDF_MARGIN_X, 8, { align: 'right' });
        pdf.line(PDF_MARGIN_X, PDF_HEADER_H - 1, pageW - PDF_MARGIN_X, PDF_HEADER_H - 1);

        const footY = pageH - PDF_FOOTER_H + 6;
        pdf.line(PDF_MARGIN_X, pageH - PDF_FOOTER_H + 1, pageW - PDF_MARGIN_X, pageH - PDF_FOOTER_H + 1);
        pdf.text(`${PDF_FOOTER_TEXT} · ${pdf.splitTextToSize(title || 'Reporte', pageW / 2)[0]}`, PDF_MARGIN_X, footY);
        pdf.text(`Página ${page} de ${total}`, pageW - PDF_MARGIN_X, footY, { align: 'right' });
    }
}

/**
 * Descarga un PDF A4 del elemento: imágenes incrustadas, saltos de página que no parten
 * elementos ni filas, encabezado con el nombre del reporte y pie con «Generado por
 * Central Media» y «Página X de Y».
 * @param {object} options landscape, backgroundColor, title (nombre del reporte), subtitle
 */
export async function exportElementToPdf(element, filename, { landscape = false, backgroundColor, title, subtitle } = {}) {
    const { canvas, layout } = await captureElementCanvas(element, { backgroundColor, withLayout: true });
    const wide = landscape || canvas.width > canvas.height * 1.15;
    const pdf = new jsPDF({
        orientation: wide ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
    });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const usableW = pageW - PDF_MARGIN_X * 2;
    const usableH = pageH - PDF_HEADER_H - PDF_FOOTER_H - 2;
    const cssWidth = layout.width || element.clientWidth || 1;
    const mmPerCss = usableW / cssWidth;
    const pxPerCss = canvas.width / cssWidth;
    const totalCss = Math.min(layout.height || canvas.height / pxPerCss, canvas.height / pxPerCss);
    const cuts = planPageBreaks(totalCss, usableH / mmPerCss, layout.boxes);

    const slice = document.createElement('canvas');
    const ctx = slice.getContext('2d');
    slice.width = canvas.width;
    cuts.forEach(([from, to], index) => {
        const sy = Math.round(from * pxPerCss);
        const sh = Math.max(1, Math.min(canvas.height - sy, Math.round((to - from) * pxPerCss)));
        slice.height = sh;
        ctx.fillStyle = backgroundColor || '#ffffff';
        ctx.fillRect(0, 0, slice.width, sh);
        ctx.drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
        if (index > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', PDF_MARGIN_X, PDF_HEADER_H + 1, usableW, (sh / pxPerCss) * mmPerCss);
    });
    drawHeaderFooter(pdf, { title: title || filename, subtitle });
    pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

function widgetHasQueryShape(query = {}) {
    return (query.dimensions || []).length > 0
        || Object.keys(query.metrics || {}).length > 0
        || (query.visibleMetrics || []).length > 0
        || (query.agg_calculated_fields || []).length > 0;
}

function applyRowView(widget, records) {
    const style = getWidgetStyle(widget);
    const query = widget.query || {};
    const dimensionKeys = query.dimensions || [];
    const requestedMetrics = visibleMetricList(query);
    const dataKeys = records[0] ? Object.keys(records[0]) : [];
    const metricKeys = requestedMetrics.filter(key => !dimensionKeys.includes(key) && (dataKeys.length === 0 || dataKeys.includes(key)));
    const headers = [...dimensionKeys, ...metricKeys].filter((key, idx, list) => list.indexOf(key) === idx && (dataKeys.length === 0 || dataKeys.includes(key)));
    const sortKey = style.sortKey || '';
    const sortDir = style.sortDir === 'asc' ? 'asc' : 'desc';
    let rows = [...records];
    if (sortKey) {
        rows.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'number' && typeof bv === 'number') {
                return sortDir === 'asc' ? av - bv : bv - av;
            }
            return sortDir === 'asc'
                ? String(av).localeCompare(String(bv), 'es')
                : String(bv).localeCompare(String(av), 'es');
        });
    }
    const limit = Number(style.topN);
    if (limit > 0) rows = rows.slice(0, limit);
    return { headers, rows };
}

function aoaFromRecords(widget, records) {
    const query = widget.query || {};
    const { headers, rows } = applyRowView(widget, records);
    if (!headers.length) {
        if (!rows.length) return [['(sin datos)']];
        const keys = Object.keys(rows[0]);
        return [keys, ...rows.map(row => keys.map(key => row[key] ?? ''))];
    }
    return [
        headers.map(key => getAlias(query, key)),
        ...rows.map(row => headers.map(key => (row[key] == null ? '' : row[key])))
    ];
}

/** Consulta el motor y devuelve records para Excel. */
export async function fetchWidgetRecords(widget, engineFilters = [], catalog = {}) {
    const query = widget?.query || {};
    if (isDecorationType(widget?.type) || isContainerType(widget?.type) || widget?.type === 'slicer') {
        return [];
    }
    const source = sourceRequestFields(query, catalog);
    if (!widgetHasQueryShape(query) || (!source.spreadsheet_id && !source.data_source_id)) {
        return [];
    }
    /* Mismo payload que el widget en pantalla: agrupación de fechas, Top N con su orden y «Otros». */
    const response = await engineFetch('/api/engine/query', {
        method: 'POST',
        body: JSON.stringify(buildWidgetQueryPayload(query, catalog, engineFilters || [], widget))
    });
    if (!response.ok) {
        throw new Error(await engineErrorMessage(response, 'No se pudieron obtener los datos para Excel.'));
    }
    const res = await response.json();
    if (!res.success) {
        throw new Error(res.detail || res.error || 'No se pudieron obtener los datos para Excel.');
    }
    return res.records || [];
}

function sheetAoaForWidget(widget, { records, filterValues } = {}) {
    const type = widget?.type;
    if (type === 'text') {
        return [['Texto'], [widget.content || '']];
    }
    if (type === 'divider') {
        return [['Elemento'], ['Separador']];
    }
    if (type === 'slicer') {
        const column = widget.query?.dimensions?.[0] || 'Filtro';
        const selected = filterValues?.[widget.filterId];
        const values = Array.isArray(selected) ? selected : (selected ? [selected] : []);
        return [
            [getAlias(widget.query, column)],
            ...(values.length ? values.map(v => [v]) : [['(sin selección)']])
        ];
    }
    if (records && records.length) return aoaFromRecords(widget, records);
    return [['(sin datos)']];
}

/** Libro .xlsx del widget (o de todos si es contenedor). */
export async function exportWidgetToExcel(widget, {
    filename,
    records,
    engineFilters = [],
    filterValues = {},
    dataSources = [],
    joins = []
} = {}) {
    const wb = XLSX.utils.book_new();
    const used = new Set();
    const append = (title, aoa) => {
        const name = uniqueSheetName(title, 'Hoja', used);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name);
    };

    if (isContainerType(widget?.type)) {
        const children = widget.children?.widgets || [];
        if (!children.length) {
            append(widget.title || 'Bloque', [['(bloque vacío)']]);
        } else {
            for (const child of children) {
                let childRecords = [];
                if (!isDecorationType(child.type) && child.type !== 'slicer') {
                    childRecords = await fetchWidgetRecords(child, engineFilters, { dataSources, joins });
                }
                append(child.title || child.type, sheetAoaForWidget(child, { records: childRecords, filterValues }));
            }
        }
    } else {
        append(widget.title || widget.type, sheetAoaForWidget(widget, { records, filterValues }));
    }

    XLSX.writeFile(wb, (filename || 'datos').endsWith('.xlsx') ? filename : `${filename || 'datos'}.xlsx`);
}
